"use client"

import SelectImageButton from "@/(admin)/components/image/select-image-button"
import { Images } from "@/constants/images"
import { Facility, MediaData, TimeLine } from "@/lib/config/types"
import { Button, FormInstance, Input, Select } from "antd"
import TextArea from "antd/es/input/TextArea"
import { t } from "i18next"
import Image from "next/image"
import { useEffect, useState } from "react"

const TimelineForm = ({ form }: { form: FormInstance }) => {
  const itinerary = form.getFieldValue("itinerary")

  const [timeLine, setTimeLine] = useState<TimeLine[]>(itinerary?.time_line || [])
  const [selectedImage, setSelectedImage] = useState<MediaData[]>([itinerary?.image || []])

  // Danh sách các icon có sẵn cho facility
  const facilityIcons = [
    { value: "distanceIcon", label: "Distance Icon", icon: Images.distanceIcon },
    { value: "bedIcon", label: "Bed Icon", icon: Images.bedIcon },
    { value: "downDistanceIcon", label: "Down Distance Icon", icon: Images.downDistanceIcon },
    { value: "upDistanceIcon", label: "Up Distance Icon", icon: Images.upDistanceIcon },
    { value: "sessionIcon", label: "Session Icon", icon: Images.sessionIcon },
  ]

  const handleSelectMedia = (media: MediaData[]) => {
    setSelectedImage(media)
  }

  const handleAddDay = () => {
    setTimeLine([
      ...timeLine,
      {
        id: timeLine.length + 1,
        time: "",
        checkpoint: "",
        description: "",
        facility: [],
      },
    ])
  }

  const handleRemoveDay = (index: number) => {
    setTimeLine(timeLine.filter((_, i) => i !== index))
  }

  const handleUpdateDay = (index: number, key: string, value: string) => {
    const updatedDays = [...timeLine]
    if (key in updatedDays[index]) {
      ;(updatedDays[index] as any)[key] = value
    }
    setTimeLine(updatedDays)
  }

  const handleAddFacility = (dayIndex: number) => {
    if (!timeLine[dayIndex]?.facility) return

    const updatedDays = [...timeLine]
    const newFacility = {
      id: updatedDays[dayIndex]?.facility?.length ? updatedDays[dayIndex]?.facility?.length + 1 : 1,
      icon_facility: "",
      description: "",
    }
    updatedDays[dayIndex]?.facility?.push(newFacility)
    setTimeLine(updatedDays)
  }

  const handleUpdateFacility = (dayIndex: number, facilityIndex: number, key: keyof Facility, value: string) => {
    setTimeLine(prevDays => {
      const updatedDays = [...prevDays]

      const updatedFacilities = [...(updatedDays[dayIndex]?.facility ?? [])]

      updatedFacilities[facilityIndex] = {
        ...updatedFacilities[facilityIndex],
        [key]: value,
      }

      updatedDays[dayIndex] = {
        ...updatedDays[dayIndex],
        facility: updatedFacilities,
      }

      return updatedDays
    })
  }

  const handleRemoveFacility = (dayIndex: number, facilityIndex: number) => {
    setTimeLine(prevDays => {
      const updatedDays = [...prevDays]
      updatedDays[dayIndex]!.facility = updatedDays[dayIndex]!.facility!.filter((_, index) => index !== facilityIndex)
      return updatedDays
    })
  }

  useEffect(() => {
    form.setFieldsValue({
      itinerary: {
        image: selectedImage?.[0],
        time_line: timeLine,
      },
    })
  }, [timeLine, selectedImage])
  return (
    <div className="gap-x-5 gap-y-2 p-5 border border-t-0 rounded-[10px] rounded-t-none border-[#f0f0f0">
      <p className="text-lg font-semibold mb-2">{t("Featured Image")}</p>
      <SelectImageButton selectedMedia={selectedImage} setSelectedMedia={handleSelectMedia} selectMode="single" />

      <label className="flex text-lg font-semibold">{t("Timeline")} Tour</label>
      <div className="grid gap-x-5 gap-y-2">
        {timeLine?.map((item, index) => (
          <div key={index} className="border p-4 rounded-md mb-2">
            <div className="flex flex-col gap-2 mb-4">
              <div className="w-full flex flex-row justify-between items-center">
                <span className="text-base font-medium text-nowrap">
                  {index + 1}. {timeLine[index].time} {timeLine[index].checkpoint}
                </span>
                <Button danger onClick={() => handleRemoveDay(index)} className="w-fit">
                  {t("Remove Timeline")}
                </Button>
              </div>
              <div className="flex gap-2 items-center text-nowrap">
                <span>{t("Time")}:</span>
                <Input
                  placeholder={t("Time")}
                  style={{ width: "15%" }}
                  value={item.time}
                  onChange={e => handleUpdateDay(index, "time", e.target.value)}
                />

                <span className="ml-4">{t("Check Point")}:</span>
                <Input
                  placeholder={t("Check Point")}
                  value={item.checkpoint}
                  onChange={e => handleUpdateDay(index, "checkpoint", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <span>{t("Description")}: </span>
                <TextArea
                  placeholder={t("Description")}
                  value={item.description}
                  autoSize
                  onChange={e => handleUpdateDay(index, "description", e.target.value)}
                />
              </div>
            </div>

            <div className="flex flex-col">
              <span className="text-base font-medium">{t("Facilities")}:</span>
              {item.facility?.map((facility, facilityIndex) => (
                <div key={facilityIndex} className="flex gap-4 mt-2  w-full items-center">
                  <div className="flex gap-2 items-center">
                    <Select
                      placeholder="Chọn icon"
                      style={{ width: 200 }}
                      value={facility.icon_facility}
                      onChange={value => handleUpdateFacility(index, facilityIndex, "icon_facility", value)}
                      options={facilityIcons.map(item => ({
                        value: item.value,
                        label: (
                          <div className="flex items-center gap-2">
                            <Image src={item.icon} alt={item.label} width={20} height={20} />
                            <span>{item.label}</span>
                          </div>
                        ),
                      }))}
                    />
                    {facility.icon_facility && (
                      <div className="flex items-center justify-center w-8 h-8 border rounded">
                        <Image
                          src={facilityIcons.find(icon => icon.value === facility.icon_facility)?.icon || ""}
                          alt="Selected icon"
                          width={20}
                          height={20}
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 items-center w-4/5">
                    <span className="text-nowrap">{t("Description")}:</span>
                    <Input
                      placeholder={t("Facility Description")}
                      value={facility.description}
                      onChange={e => handleUpdateFacility(index, facilityIndex, "description", e.target.value)}
                    />
                  </div>

                  <Button danger onClick={() => handleRemoveFacility(index, facilityIndex)}>
                    {t("Remove Item")}
                  </Button>
                </div>
              ))}
              <Button type="primary" onClick={() => handleAddFacility(index)} className="mt-2 w-fit">
                {t("Add Item")}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Button type="primary" onClick={handleAddDay} className="mt-2">
        {t("Add Timeline")}
      </Button>
    </div>
  )
}

export default TimelineForm
